import { Injectable } from '@angular/core';
import { DrupalRESTService } from './drupal-rest.service';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';

interface PrintOptions {
  title: string;
  styles?: string;
  promptPrint?: boolean;
  additionalHeadContent?: string;
  showSchoolName?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PrintService {

  private normalizeStyles = `
    /* normalize.css v8.0.1 */
    html{line-height:1.15;-webkit-text-size-adjust:100%}
    body{margin:0}
    main{display:block}
    h1{font-size:2em;margin:.67em 0}
    hr{box-sizing:content-box;height:0;overflow:visible}
    pre{font-family:monospace,monospace;font-size:1em}
    a{background-color:transparent}
    abbr[title]{border-bottom:none;text-decoration:underline;text-decoration:underline dotted}
    b,strong{font-weight:bolder}
    code,kbd,samp{font-family:monospace,monospace;font-size:1em}
    small{font-size:80%}
    sub,sup{font-size:75%;line-height:0;position:relative;vertical-align:baseline}
    sub{bottom:-.25em}
    sup{top:-.5em}
    img{border-style:none}
    button,input,optgroup,select,textarea{font-family:inherit;font-size:100%;line-height:1.15;margin:0}
    button,input{overflow:visible}
    button,select{text-transform:none}
    button,[type="button"],[type="reset"],[type="submit"]{-webkit-appearance:button}
    button::-moz-focus-inner,[type="button"]::-moz-focus-inner,[type="reset"]::-moz-focus-inner,[type="submit"]::-moz-focus-inner{border-style:none;padding:0}
    button:-moz-focusring,[type="button"]:-moz-focusring,[type="reset"]:-moz-focusring,[type="submit"]:-moz-focusring{outline:1px dotted ButtonText}
    fieldset{padding:.35em .75em .625em}
    legend{box-sizing:border-box;color:inherit;display:table;max-width:100%;padding:0;white-space:normal}
    progress{vertical-align:baseline}
    textarea{overflow:auto}
    [type="checkbox"],[type="radio"]{box-sizing:border-box;padding:0}
    [type="number"]::-webkit-inner-spin-button,[type="number"]::-webkit-outer-spin-button{height:auto}
    [type="search"]{-webkit-appearance:textfield;outline-offset:-2px}
    [type="search"]::-webkit-search-decoration{-webkit-appearance:none}
    ::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}
    details{display:block}
    summary{display:list-item}
    template{display:none}
    [hidden]{display:none}
  `;

  private defaultStyles = `
    /* Standard print margins - 1 inch on all sides */
    @page { 
      size: letter portrait; 
      margin: 1in;
    }

    /* CRITICAL: Reset all page breaks to prevent unwanted gaps */
    * {
      page-break-inside: auto !important;
      page-break-before: auto !important;
      page-break-after: auto !important;
      break-inside: auto !important;
      break-before: auto !important;
      break-after: auto !important;
    }

    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 12px;
      line-height: 1.3;
      color: #000;
      /* Add padding for better content spacing */
      margin: 0;
      padding: 0.25in;
      height: auto !important;
      overflow: visible !important;
    }

    /* Header Styles */
    .report-title {
      font-size: 18px;
      margin: 10px 0 5px 0;
      text-align: center;
    }

    .report-period {
      font-size: 14px;
      margin: 0 0 15px 0;
      text-align: center;
    }

    .school-name {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 10px;
      text-align: center;
    }

    /* Table Styles */
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 15px;
    }

    th {
      background-color: #f0f0f0;
      text-align: left;
      font-weight: bold;
      border: 1px solid #ddd;
      padding: 5px;
    }

    td {
      border: 1px solid #ddd;
      padding: 5px;
      vertical-align: top;
    }

    /* Section Styles */
    .section {
      margin-bottom: 20px;
    }

    .section h3 {
      font-size: 16px;
      margin: 10px 0 5px 0;
      border-bottom: 1px solid #ccc;
      padding-bottom: 3px;
    }

    /* Student Details Section */
    .student-section {
      margin-top: 15px;
      border-top: 1px solid #ccc;
      padding-top: 5px;
    }

    .student-section h4 {
      font-size: 14px;
      margin: 10px 0 5px 0;
    }

    .student-section h5 {
      font-size: 12px;
      margin: 8px 0 3px 0;
    }

    /* Compact tables for detailed sections */
    .print-compact table {
      font-size: 10px;
    }

    .print-compact th,
    .print-compact td {
      padding: 3px;
    }

    /* Hide elements for print */
    @media print {
      .no-print {
        display: none !important;
      }

      /* Force everything to stay together and prevent unwanted page breaks */
      body, html {
        height: auto !important;
        overflow: visible !important;
      }
      
      /* Reset all page break rules to auto for natural flow */
      * {
        page-break-inside: auto !important;
        page-break-before: auto !important;
        page-break-after: auto !important;
        break-inside: auto !important;
        break-before: auto !important;
        break-after: auto !important;
      }

      /* Standard print margins - 1 inch on all sides for consistency */
      @page {
        margin: 1in;
      }
      
      /* Additional body padding for print */
      body {
        padding: 0.25in;
      }
    }
  `;

  constructor(
    private _drupalRESTService: DrupalRESTService,
    private _authService: AuthService
  ) {}

  /**
   * Get the studio display name fresh each time from the API,
   * using the currently selected studio's ID.
   */
  private loadStudioDisplayName(): Promise<string> {
    return new Promise((resolve) => {
      this._drupalRESTService.httpGET('/api_rest/v1/getStudioInformation')
        .subscribe({
          next: (data: any) => {
            resolve(data?.field_studio_display_name?.[0]?.value || '');
          },
          error: error => {
            LoggerService.error('Error loading studio display name:', error);
            resolve('');
          }
        });
    });
  }

  /**
   * Open the print window synchronously, inside the click handler.
   *
   * Transient user activation is lost once the click's synchronous call stack
   * unwinds into a network callback. An XHR/fetch response is not a user
   * gesture, no matter how fast it returns. Reports that fetch before printing
   * must therefore call THIS first (while still in the handler), then pass the
   * resulting handle to print(). Returns null if the popup was blocked anyway,
   * which the caller should surface rather than failing silently.
   */
  openPrintWindow(): Window | null {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return null;

    // Placeholder so the tab isn't blank while the report data is in flight.
    printWindow.document.write(
      '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Preparing…</title></head>' +
      '<body style="font-family:Arial,Helvetica,sans-serif;padding:2rem;color:#444">' +
      'Preparing the printout…</body></html>'
    );
    return printWindow;
  }

  /**
   * @param existingWindow A window from openPrintWindow(), for callers that had
   *   to fetch data first. Omit to open one here (safe only when called
   *   directly from a user gesture).
   */
  print(content: string, options: PrintOptions, existingWindow?: Window | null) {
    // Open the window immediately (synchronous with user click) to avoid popup blockers
    const printWindow = existingWindow ?? window.open('', '_blank');
    if (!printWindow) return;

    if (options.showSchoolName) {
      // Fetch the studio name for the currently selected studio, then write content
      this.loadStudioDisplayName().then(studioDisplayName => {
        this.writePrintContent(printWindow, content, options, studioDisplayName);
      });
    } else {
      this.writePrintContent(printWindow, content, options, '');
    }
  }

  private writePrintContent(printWindow: Window, content: string, options: PrintOptions, studioDisplayName: string) {
    const combinedStyles = `
      ${this.normalizeStyles}
      ${this.defaultStyles}
      ${options.styles || ''}
    `;

    const schoolNameHtml = options.showSchoolName && studioDisplayName
      ? `<div class="school-name">${studioDisplayName}</div>`
      : '';

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <title>${options.title}</title>
          <style>${combinedStyles}</style>
          ${options.additionalHeadContent || ''}
        </head>
        <body>
          <div class="report-header">
            ${schoolNameHtml}
            ${content}
          </div>
          <div class="no-print">
            <button onclick="window.print()">Print</button>
            <button onclick="window.close()">Close</button>
          </div>
          ${options.promptPrint ? this.promptPrintScript() : ''}
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  }

  /**
   * Prints a complete, self-contained HTML document (its own head, styles and
   * body), a designed piece such as a gift certificate, rather than report
   * content wrapped in the default report chrome. Uses the same popup and
   * print-prompt handling as print().
   */
  printDocument(documentHtml: string, existingWindow?: Window | null, promptPrint = true) {
    const printWindow = existingWindow ?? window.open('', '_blank');
    if (!printWindow) return;

    const script = promptPrint ? this.promptPrintScript() : '';
    const html = /<\/body>/i.test(documentHtml)
      ? documentHtml.replace(/<\/body>/i, `${script}</body>`)
      : documentHtml + script;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  }

  private promptPrintScript(): string {
    return `<script>
            // Chrome/Edge print a BLANK page if print() fires before the
            // document written via document.write has been laid out; onload
            // alone is not enough. Wait for fonts (when supported) plus two
            // animation frames so the first paint has happened.
            (function () {
              var fire = function () {
                requestAnimationFrame(function () {
                  requestAnimationFrame(function () { window.print(); });
                });
              };
              // Do NOT close() unconditionally: "Save as PDF" leaves the
              // dialog's result pending, and tearing the window down can drop
              // the job. Close only after a real print completes.
              window.onafterprint = function () { window.close(); };
              if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(fire).catch(fire);
              } else {
                fire();
              }
            })();
          <\/script>`;
  }

  /**
   * Simple print that uses the browser's native print functionality
   */
  simplePrint() {
    window.print();
  }
}

